<template>
  <div class="container-fluid px-4 mt-4">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <h5 class="fw-bold mb-0">운영중 정기권 목록</h5>
      <span class="text-muted small" v-if="selectedArea && passes.length > 0">
        총 {{ passes.length }}명
      </span>
    </div>

    <div class="card shadow-sm mb-4">
      <div class="card-body p-4">
        <h6 class="fw-bold mb-3">필터 선택</h6>
        <div class="row g-3">
          <div class="col-md-8">
            <label class="form-label small fw-bold mb-2 d-block">주차장</label>
            <select v-model="selectedArea" class="form-select" @change="fetchActivePasses">
              <option value="">-- 주차장 선택 --</option>
              <option v-for="a in areas" :key="a.AreaSeq" :value="a.AreaSeq">
                {{ a.AreaName }} ({{ a.AreaCode }})
              </option>
            </select>
          </div>
          <div class="col-md-4">
            <label class="form-label small fw-bold mb-2 d-block">결제 미이행월</label>
            <div class="input-group">
              <input 
                v-model="filterMonth" 
                type="month" 
                class="form-control" 
                @change="fetchActivePasses"
                title="결제하지 않은 사용자만 필터링"
              />
              <button 
                v-if="filterMonth" 
                type="button" 
                class="btn btn-outline-secondary" 
                @click="filterMonth = ''; fetchActivePasses()"
                title="필터 초기화"
              >
                ✕
              </button>
            </div>
            <small v-if="filterMonth" class="text-muted d-block mt-1">
              {{ formatYearMonth(filterMonth) }}에 미결제
            </small>
          </div>
        </div>
      </div>
    </div>

    <div v-if="selectedArea">
      <div v-if="loading" class="text-center py-5 text-muted">불러오는 중...</div>
      <div v-if="loadError" class="alert alert-danger">{{ loadError }}</div>

      <div class="card shadow-sm mb-4">
        <div class="card-body p-4">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <h6 class="fw-bold mb-0">운영중 정기권 ({{ passes.length }}명)</h6>
            <div class="d-flex gap-2">
              <button v-if="passes.length > 0" type="button" class="btn btn-sm btn-outline-info" @click="openCardSmsModal">
                💳 카드 청구서 문자
              </button>
              <button v-if="passes.length > 0" type="button" class="btn btn-sm btn-outline-warning" @click="openCashSmsModal">
                💵 현금 청구서 문자
              </button>
              <button v-if="passes.length > 0" type="button" class="btn btn-sm btn-outline-success" @click="exportToExcel">
                📥 엑셀 다운로드
              </button>
              <button v-if="passes.length > 0" type="button" class="btn btn-sm btn-outline-danger" @click="confirmDeleteArea">
                정기권 삭제
              </button>
            </div>
          </div>

          <div v-if="passes.length === 0" class="text-center text-muted py-4">
            운영중인 정기권이 없습니다.
          </div>

          <div v-else class="table-responsive">
            <table class="table table-hover align-middle table-sm">
              <thead class="table-dark">
                <tr>
                  <th style="width: 30px">
                    <input type="checkbox" class="form-check-input" :checked="checkAllChecked" @change="toggleCheckAll">
                  </th>
                  <th style="width: 60px">#</th>
                  <th>청구서</th>
                  <th>사용자 ID</th>
                  <th>이름</th>
                  <th>차량번호</th>
                  <th>요금제</th>
                  <th>할인</th>
                  <th style="width: 160px;">시작</th>
                  <th style="width: 160px;">종료</th>
                  <th>상태</th>
                  <th style="width: 100px;">최근결제월</th>
                  <th style="width: 180px;">등록일</th>
                  <th>최근청구명</th>
                  <th>청구서</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in passes" :key="p.PassSeq">
                  <td>
                    <input type="checkbox" class="form-check-input" v-model="checked[p.PassSeq]">
                  </td>
                  <td class="text-muted small">{{ p.PassSeq }}</td>
                  <td class="text-muted small">{{ p.WaitingSeq || '-' }}</td>
                  <td class="fw-500">{{ p.UserID }}</td>
                  <td>{{ p.UserName }}</td>
                  <td class="font-monospace small">{{ p.CarNumber }}</td>
                  <td>{{ p.FeeName }}</td>
                  <td>{{ p.DiscountName || '없음' }}</td>
                  <td>{{ p.StartDate }}</td>
                  <td>{{ p.EndDate }}</td>
                  <td>
                    <span class="badge bg-success" style="font-size:0.75rem;" v-if="p.Status === 'ACTIVE'">ACTIVE</span>
                    <span class="badge bg-secondary" style="font-size:0.75rem;" v-else>{{ p.Status }}</span>
                  </td>
                  <td class="small text-center">
                    <span v-if="p.LastPaidMonth" class="badge bg-primary">{{ formatLastPaidMonth(p.LastPaidMonth) }}</span>
                    <span v-else class="text-muted small">-</span>
                  </td>
                  <td class="small text-muted">{{ p.CreatedDate }}</td>
                  <td class="small text-truncate">{{ p.LastBillName || '-' }}</td>
                  <td class="text-center">
                    <div class="d-inline-flex align-items-center gap-1">
                      <button v-if="p.WaitingSeq" class="btn btn-sm btn-outline-primary" @click="openPaymentDetail(p)" title="청구서 보기">
                        📄
                      </button>
                      <button v-if="p.WaitingSeq" class="btn btn-sm btn-outline-danger" @click="confirmDeletePass(p)" title="청구서 및 정기권 삭제">
                        삭제
                      </button>
                      <small v-else class="text-muted">-</small>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!selectedArea" class="text-center text-muted py-5">
      주차장을 선택하면 운영중인 정기권 목록이 표시됩니다.
    </div>
  </div>

  <CardInvoiceSmsModal
    :isOpen="cardSmsModalOpen"
    :selectedItems="passes.filter(p => checked[p.PassSeq])"
    :areas="areas"
    :selectedAreaSeq="selectedArea"
    @close="cardSmsModalOpen = false"
    @bulk-sms-sent="onCardSmsSent"
  />
  <CashInvoiceSmsModal
    :isOpen="cashSmsModalOpen"
    :selectedItems="passes.filter(p => checked[p.PassSeq])"
    :areas="areas"
    :selectedAreaSeq="selectedArea"
    @close="cashSmsModalOpen = false"
    @bulk-sms-sent="onCashSmsSent"
  />
  <!-- 결제 상세 모달 (컴포넌트) -->
  <PaymentDetailModal
    :isOpen="paymentDetailModal"
    :target="paymentDetailTarget"
    :payments="paymentDetailsList"
    :processing="paymentDetailProcessing"
    :error="paymentDetailError"
    @close="closePaymentDetail"
    @complete-payment="completePaymentManually"
    @delete-payment="deletePaymentRequest"
  />

  <!-- Active Pass Delete Modal -->
  <div v-if="deletePassModal" class="modal d-block" tabindex="-1" style="background: rgba(0,0,0,0.5);">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header bg-light">
          <div>
            <h5 class="modal-title fw-bold">청구서 및 정기권 삭제 확인</h5>
            <small class="text-muted">WaitingSeq: {{ deletePassTarget?.WaitingSeq }}</small>
          </div>
          <button type="button" class="btn-close" @click="deletePassModal = false"></button>
        </div>
        <div class="modal-body">
          <p class="mb-3">
            <strong>{{ deletePassTarget?.UserID }}</strong> ({{ deletePassTarget?.UserName }}) — 차량 <strong>{{ deletePassTarget?.CarNumber }}</strong><br />
            <span class="text-muted small">주차장: {{ deletePassTarget?.AreaName }}</span><br />
            <span class="text-danger small">이 작업은 되돌릴 수 없습니다. 청구서와 연결된 정기권을 모두 삭제합니다.</span>
          </p>
          <div v-if="deletePassPaymentRequests.length > 0" class="mb-3">
            <div class="mb-2"><strong>삭제될 청구서 목록</strong> (총 {{ deletePassPaymentRequests.length }}건)</div>
            <div class="table-responsive" style="max-height: 240px; overflow-y: auto;">
              <table class="table table-sm mb-0">
                <thead>
                  <tr>
                    <th class="py-2">청구번호</th>
                    <th class="py-2">청구명</th>
                    <th class="py-2">금액</th>
                    <th class="py-2">상태</th>
                    <th class="py-2">결제방식</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="pm in deletePassPaymentRequests" :key="pm.PaymentSeq">
                    <td class="py-2 text-muted small">{{ pm.PaymentSeq }}</td>
                    <td class="py-2">{{ pm.BillName || '-' }}</td>
                    <td class="py-2">{{ pm.PaymentPrice?.toLocaleString() || 0 }}원</td>
                    <td class="py-2">
                      <span v-if="pm.Status === 'PAID'" class="badge bg-success">결제완료</span>
                      <span v-else-if="pm.Status === 'READY'" class="badge bg-info">결제전</span>
                      <span v-else class="badge bg-secondary">{{ pm.Status }}</span>
                    </td>
                    <td class="py-2">
                      <span v-if="pm.PaymentType === 'CARD'" class="badge bg-info">카드</span>
                      <span v-else-if="pm.PaymentType === 'CASH'" class="badge bg-warning">현금</span>
                      <span v-else class="badge bg-secondary">{{ pm.PaymentType || '-' }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div v-else class="alert alert-light py-2 mb-3 small text-muted">
            이 신청건에 연결된 청구서가 없습니다.
          </div>
          <div v-if="deletePassError" class="alert alert-danger py-1 small">{{ deletePassError }}</div>
        </div>
        <div class="modal-footer bg-light">
          <button type="button" class="btn btn-secondary" @click="deletePassModal = false">취소</button>
          <button type="button" class="btn btn-danger" :disabled="deletePassSaving" @click="doDeletePass">{{ deletePassSaving ? '삭제 중...' : '삭제' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import CardInvoiceSmsModal from '../../components/CardInvoiceSmsModal.vue'
import CashInvoiceSmsModal from '../../components/CashInvoiceSmsModal.vue'
import PaymentDetailModal from '../../components/PaymentDetailModal.vue'

export default {
  name: 'AdminActiveParkingPassView',
  components: { CardInvoiceSmsModal, CashInvoiceSmsModal, PaymentDetailModal },
  data() {
    return {
      areas: [],
      selectedArea: '',
      passes: [],
      checked: {},
      cardSmsModalOpen: false,
      cashSmsModalOpen: false,
      showPaymentsModal: false,
      paymentsList: [],
      paymentDetailsList: [],
      modalWaitingSeq: null,
      paymentDetailTarget: null,
      paymentDetailModal: false,
      loading: false,
      loadError: null,
      paymentDetailProcessing: false,
      paymentDetailError: null,
      deletePassModal: false,
      deletePassTarget: null,
      deletePassPaymentRequests: [],
      deletePassError: null,
      deletePassSaving: false,
      filterMonth: '' // YYYY-MM 형식 (예: "2026-09")
    }
  },
  mounted() {
    this.initializeAreas()
  },
  computed: {
    checkAllChecked() {
      return this.passes.length > 0 && this.getCheckedCount() === this.passes.length
    }
  },
  methods: {
    async initializeAreas() {
      await this.fetchAreas()
      if (this.areas.length > 0) {
        this.selectedArea = this.areas[0].AreaSeq
        await this.fetchActivePasses()
      }
    },
    async confirmDeleteArea() {
      if (!this.selectedArea) return;
      const area = this.areas.find(a => a.AreaSeq === this.selectedArea) || {};
      const areaName = area.AreaName || '선택된 주차장';
      const count = this.passes.length || 0;
      const ok = window.confirm(`${areaName} - 삭제할 정기권수: ${count}\n정말 삭제하시겠습니까?\n(삭제 후 복구 불가능합니다)`);
      if (!ok) return;
      await this.deleteActivePasses();
    },

    async deleteActivePasses() {
      if (!this.selectedArea) return;
      try {
        const res = await axios.delete(`${process.env.VUE_APP_API_BASE}/api/admin/active-parking-pass/area/${this.selectedArea}`);
        if (res && res.data && res.data.success) {
          alert(`삭제되었습니다. 삭제 수: ${res.data.deleted}`);
          await this.fetchActivePasses();
        } else {
          const msg = (res && res.data && res.data.message) ? res.data.message : '삭제에 실패했습니다.';
          alert(msg);
        }
      } catch (e) {
        console.error('deleteActivePasses error:', e);
        alert('삭제 중 오류가 발생했습니다. 콘솔을 확인하세요.');
      }
    }
,
    async fetchAreas() {
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/parking-areas`)
        this.areas = res.data
      } catch (e) {
        console.error('fetchAreas error:', e)
      }
    },
    async fetchActivePasses() {
      if (!this.selectedArea) {
        this.passes = []
        return
      }
      this.loading = true
      this.loadError = null
      try {
        // YYYY-MM 형식을 YYYYMM 형식으로 변환
        let queryUrl = `${process.env.VUE_APP_API_BASE}/api/admin/active-parking-pass?areaSeq=${this.selectedArea}`
        if (this.filterMonth) {
          // "2026-09" → "202609"
          const yyyymm = this.filterMonth.replace('-', '')
          queryUrl += `&filterMonth=${yyyymm}`
        }
        const res = await axios.get(queryUrl)
        this.passes = res.data || []
      } catch (e) {
        this.loadError = '운영중 정기권을 불러오지 못했습니다.'
        console.error('fetchActivePasses error:', e)
      } finally {
        this.loading = false
      }
    }
    ,
    formatYearMonth(dateStr) {
      // "2026-09" 형식을 "2026년 9월"로 변환
      if (!dateStr) return '';
      const [year, month] = dateStr.split('-');
      const monthNum = parseInt(month, 10);
      return `${year}년 ${monthNum}월`;
    },
    formatLastPaidMonth(lastPaidMonth) {
      // 202609 형식을 "2026-09"로 변환
      if (!lastPaidMonth) return '-';
      const str = String(lastPaidMonth).padStart(6, '0');
      if (str.length !== 6) return '-';
      const year = str.substring(0, 4);
      const month = str.substring(4, 6);
      return `${year}-${month}`;
    }
    ,
    async openPaymentDetail(pass) {
      if (!pass || !pass.WaitingSeq) return;
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/payment-request/list/${pass.WaitingSeq}`);
        this.paymentDetailTarget = pass;
        this.paymentDetailsList = res.data || [];
        this.paymentDetailError = null;
        this.paymentDetailModal = true;
        
        // Legacy support
        this.modalWaitingSeq = pass.WaitingSeq;
        this.paymentsList = res.data || [];
        this.showPaymentsModal = true;
      } catch (e) {
        console.error('openPaymentDetail error:', e);
        this.paymentDetailError = e.response?.data?.message || '결제 내역을 불러오지 못했습니다.';
      }
    },
    closePaymentDetail() {
      this.paymentDetailModal = false;
      this.paymentDetailTarget = null;
      this.paymentDetailsList = [];
      this.paymentDetailError = null;
      
      // Legacy support
      this.showPaymentsModal = false;
      this.paymentsList = [];
      this.modalWaitingSeq = null;
    },
    closePayments() {
      this.closePaymentDetail();
    },
    async openPayments(waitingSeq) {
      if (!waitingSeq) return;
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/payment-request/list/${waitingSeq}`);
        this.paymentsList = res.data || [];
        this.modalWaitingSeq = waitingSeq;
        this.showPaymentsModal = true;
      } catch (e) {
        console.error('openPayments error:', e);
        alert('결제 내역을 불러오지 못했습니다. 콘솔을 확인하세요.');
      }
    },
    async confirmDeletePass(pass) {
      if (!pass || !pass.WaitingSeq) return;
      this.deletePassTarget = pass;
      this.deletePassError = null;
      this.deletePassPaymentRequests = [];
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/payment-request/list/${pass.WaitingSeq}`);
        this.deletePassPaymentRequests = res.data || [];
      } catch (e) {
        console.warn('delete pass payment requests load failed:', e);
        this.deletePassPaymentRequests = [];
      }
      this.deletePassModal = true;
    },
    async doDeletePass() {
      if (!this.deletePassTarget || !this.deletePassTarget.PassSeq) return;
      if (!confirm('이 정기권을 삭제하면 복구할 수 없습니다. 해당 정기권에 연결된 모든 청구서가 삭제됩니다. 계속하시겠습니까?')) return;
      this.deletePassSaving = true;
      this.deletePassError = null;
      try {
        await axios.delete(`${process.env.VUE_APP_API_BASE}/api/admin/active-parking-pass/${this.deletePassTarget.PassSeq}`);
        this.deletePassModal = false;
        await this.fetchActivePasses();
        alert('정기권이 삭제되었습니다.');
      } catch (e) {
        console.error('delete pass error:', e);
        this.deletePassError = e.response?.data?.message || '삭제에 실패했습니다.';
      } finally {
        this.deletePassSaving = false;
      }
    },
    async completePaymentManually(payment) {
      this.paymentDetailProcessing = true;
      this.paymentDetailError = null;
      try {
        await axios.post(`${process.env.VUE_APP_API_BASE}/api/admin/payment-request/complete-manual`, {
          paymentSeq: payment.PaymentSeq,
          waitingSeq: this.paymentDetailTarget.WaitingSeq
        });
        this.closePaymentDetail();
        await this.fetchActivePasses();
        alert('결제 처리가 완료되었습니다.');
      } catch (e) {
        console.error('completePaymentManually error:', e);
        this.paymentDetailError = e.response?.data?.message || '결제 완료 처리에 실패했습니다.';
        alert(this.paymentDetailError);
      } finally {
        this.paymentDetailProcessing = false;
      }
    },
    async deletePaymentRequest(payment) {
      this.paymentDetailProcessing = true;
      this.paymentDetailError = null;
      try {
        await axios.delete(`${process.env.VUE_APP_API_BASE}/api/admin/payment-request/${payment.PaymentSeq}`);
        this.closePaymentDetail();
        await this.fetchActivePasses();
        alert('청구서가 삭제되었습니다.');
      } catch (e) {
        console.error('deletePaymentRequest error:', e);
        this.paymentDetailError = e.response?.data?.message || '청구서 삭제에 실패했습니다.';
        alert(this.paymentDetailError);
      } finally {
        this.paymentDetailProcessing = false;
      }
    },
    exportToExcel() {
      if (this.passes.length === 0) {
        alert('다운로드할 정기권이 없습니다.');
        return;
      }

      const escapeXml = value => {
        const str = String(value || '');
        return str.replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&apos;');
      };

      const areaName = this.areas.find(a => a.AreaSeq === parseInt(this.selectedArea))?.AreaName || '주차장';
      const timestamp = new Date().toISOString().slice(0, 10);
      const fileName = `운영중정기권_${areaName}_${timestamp}.xls`;

      const cols = [
        { width: 50 },
        { width: 80 },
        { width: 120 },
        { width: 100 },
        { width: 100 },
        { width: 100 },
        { width: 80 },
        { width: 220 },
        { width: 220 },
        { width: 80 },
        { width: 240 },
        { width: 160 }
      ];

      const headers = ['#', 'WaitingSeq', '사용자ID', '이름', '차량번호', '요금제', '할인', '시작', '종료', '상태', '등록일', '최근청구명'];

      const rows = this.passes.map((p, idx) => [
        idx + 1,
        p.WaitingSeq || '-',
        p.UserID || '',
        p.UserName || '',
        p.CarNumber || '',
        p.FeeName || '',
        p.DiscountName || '없음',
        p.StartDate || '-',
        p.EndDate || '-',
        p.Status || '-',
        p.CreatedDate || '-',
        p.LastBillName || '-'
      ]);

      const xml = `<?xml version="1.0"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
          xmlns:o="urn:schemas-microsoft-com:office:office"
          xmlns:x="urn:schemas-microsoft-com:office:excel"
          xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
          xmlns:html="http://www.w3.org/TR/REC-html40">
  <DocumentProperties xmlns="urn:schemas-microsoft-com:office:office">
    <Author>EFPark</Author>
    <Created>${new Date().toISOString()}</Created>
    <Version>16.00</Version>
  </DocumentProperties>
  <Styles>
    <Style ss:ID="Header">
      <Font ss:Bold="1"/>
      <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
      <Borders>
        <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1"/>
        <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1"/>
        <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1"/>
        <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1"/>
      </Borders>
    </Style>
    <Style ss:ID="Default">
      <Alignment ss:Vertical="Center"/>
      <Borders>
        <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1"/>
        <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1"/>
        <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1"/>
        <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1"/>
      </Borders>
    </Style>
  </Styles>
  <Worksheet ss:Name="운영중정기권">
    <Table>
      ${cols.map(col => `<Column ss:Width="${col.width}"/>`).join('')}
      <Row ss:StyleID="Header">
        ${headers.map(h => `<Cell><Data ss:Type="String">${escapeXml(h)}</Data></Cell>`).join('')}
      </Row>
      ${rows.map(row => `
      <Row>
        ${row.map(cell => `<Cell><Data ss:Type="String">${escapeXml(cell)}</Data></Cell>`).join('')}
      </Row>
      `).join('')}
    </Table>
  </Worksheet>
</Workbook>`;

      const blob = new Blob([xml], { type: 'application/vnd.ms-excel' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    getCheckedCount() {
      return Object.values(this.checked).filter(v => v).length
    },
    toggleCheckAll() {
      const allChecked = this.getCheckedCount() === this.passes.length
      this.checked = {}
      if (!allChecked) {
        this.passes.forEach(p => {
          this.checked[p.PassSeq] = true
        })
      }
    },
    openCardSmsModal() {
      if (this.getCheckedCount() === 0) {
        alert('문자를 보낼 정기권을 선택하세요.')
        return
      }
      this.cardSmsModalOpen = true
    },
    openCashSmsModal() {
      if (this.getCheckedCount() === 0) {
        alert('문자를 보낼 정기권을 선택하세요.')
        return
      }
      this.cashSmsModalOpen = true
    },
    onCardSmsSent() {
      // 발송 완료 후 처리: 모달은 유지하고 목록만 갱신합니다.
      this.fetchActivePasses()
    },
    onCashSmsSent() {
      // 발송 완료 후 처리: 모달은 유지하고 목록만 갱신합니다.
      this.fetchActivePasses()
    }
  }
}
</script>

<style scoped>
.table { font-size: 0.875rem; }
.fw-500 { font-weight: 500; }
.font-monospace { font-family: 'Courier New', monospace; }
</style>
